import { useCallback } from 'react'
import { InsurancePlan, InsurancePlanType } from '@typesApp/InsurancePlan'
import { CommerceBenefitPlanResponse } from '@typesApp/Service'
import { PREFER_CACHE } from '@constants/index'
import { useAppTranslation } from '@hooks/useAppTranslation'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useEnvironment } from '@hooks/useEnvironment'
import { useLazyGetInsurancePlansQuery } from '@redux/queries/claimRecords'
import { BenefitDetails, getBenefitPlanDetails } from '@utils/benefitPlanDetails'
import {
  getBenefitRenewalDate,
  getDecoratedInsurancePlans,
  getFrameWithLensesCalloutTranslationKey,
} from '@utils/benefitPlanUtil'
import { DateUtil } from '@utils/dateUtil'

type AdjustedText = {
  benefitDescription: string
  copayInformation?: string
}

const EMPTY_RESPONSE = {} as CommerceBenefitPlanResponse

const UNAVAILABLE_RESPONSE = {
  status: 'unavailable',
} as CommerceBenefitPlanResponse

type UseBenefitPlanServiceResult = {
  getBenefitPlan: () => Promise<CommerceBenefitPlanResponse>
}

/**
 * @returns simplified benefit plan data for commerce F/E
 */
export const useBenefitPlanService = (): UseBenefitPlanServiceResult => {
  const { dateFormat } = useEnvironment()
  const { t } = useAppTranslation()
  const { claimRecordId } = useClaimRecord()
  const [triggerFetchPlans] = useLazyGetInsurancePlansQuery()

  const getBenefitPlan = useCallback(async (): Promise<CommerceBenefitPlanResponse> => {
    const adjustBenefitText = (details: BenefitDetails, planType: InsurancePlanType): AdjustedText => {
      const { serviceDescription, isComingSoon, isStatusAvailable, isCopay, showFallbackDescription, benefitItem } =
        details

      let benefitDescription = serviceDescription
      const renewlDate = getBenefitRenewalDate(benefitItem, dateFormat)

      if (isComingSoon) {
        benefitDescription = t(`InsurancePlans.benefits-coming-soon.${planType}`)
      } else if (!isStatusAvailable && renewlDate) {
        benefitDescription = t('InsurancePlans.benefit-renewal-unformatted', {
          date: DateUtil.fromISODate(renewlDate, dateFormat),
        })
      }

      if (planType === 'FRAME' && showFallbackDescription) {
        benefitDescription = t('InsurancePlans.service-description.frame')
      }

      if (planType === 'LENSES' && showFallbackDescription) {
        benefitDescription = t('InsurancePlans.service-description.lenses')
      }

      if (planType === 'FRAME_WITH_LENSES') {
        const baseDescription = t('InsurancePlans.service-description-frame-with-lenses')

        const calloutTranslationKey = getFrameWithLensesCalloutTranslationKey(benefitItem, true)
        const calloutText = calloutTranslationKey ? t(calloutTranslationKey, '') : ''

        benefitDescription = `${baseDescription} ${calloutText}`
      }

      const copayInformation = isCopay ? t('InsurancePlans.copay-conditions') : undefined

      return {
        benefitDescription: benefitDescription ?? '',
        copayInformation,
      }
    }

    const transformResponse = (plans: InsurancePlan[]): CommerceBenefitPlanResponse => {
      const decoratedPlans = getDecoratedInsurancePlans(plans)

      const benefits: CommerceBenefitPlanResponse['benefits'] = decoratedPlans.map(({ plan, variant }) => {
        const details = getBenefitPlanDetails(plan, variant)
        const { benefitDescription, copayInformation } = adjustBenefitText(details, plan.planType)

        return {
          planType: plan.planType,
          benefitStatus: details?.benefitItem?.benefitStatus ?? 'BENEFITS_NOT_AVAILABLE',
          benefitAvailability: details?.benefitItem?.benefitAvailability ?? 'NOT_SUPPORTED',
          benefitDescription,
          copayInformation,
        }
      })

      return {
        status: benefits.length ? 'available' : 'unavailable',
        benefits,
      }
    }

    if (!claimRecordId) {
      return UNAVAILABLE_RESPONSE
    }

    try {
      const plans = await triggerFetchPlans({ claimRecordId }, PREFER_CACHE).unwrap()
      return transformResponse(plans)
    } catch (error) {
      return EMPTY_RESPONSE
    }
  }, [triggerFetchPlans, claimRecordId, dateFormat, t])

  return {
    getBenefitPlan,
  }
}
